"use client";

import { useState } from "react";

import ReactMarkdown from "react-markdown";

import type { ContentJson } from "@kit/integrations";
import { SeoCheck } from "@kit/integrations";
import { Button } from "@kit/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@kit/ui/card";
import { Input } from "@kit/ui/input";
import { Label } from "@kit/ui/label";
import { Separator } from "@kit/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@kit/ui/tabs";
import { cn } from "@kit/ui/utils";

export default function SeoAnalystClient() {
  const [loading, setLoading] = useState(false);
  const [content, setContent] = useState<any>();
  const [analysis, setAnalysis] = useState<any>();
  const [error, setError] = useState<string | null>(null);

  const handleUrlSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const formData = new FormData(e.currentTarget);
      const url = formData.get("url") as string;
      const targetKeyword = formData.get("targetKeyword") as string;
      const response = await fetch("/api/seo/analyze", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ url }),
      });

      if (!response.ok) {
        throw new Error("Failed to analyze URL");
      }

      const data = await response.json();

      const contentResult: ContentJson = {
        ...data,
        keyword: targetKeyword,
        subKeywords: (formData.get("subKeywords") as string)
          .split(",")
          .map((k) => k.trim()),
        languageCode: "en",
        countryCode: "us",
      };

      setContent(contentResult);

      const seoCheck = new SeoCheck(contentResult, new URL(url).hostname);
      const result = await seoCheck.analyzeSeo();
      setAnalysis(result);
    } catch (err) {
      console.error("Error details:", err);
      setError(
        "Failed to analyze webpage. Please check the URL and try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  const _handleAdvancedSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const formData = new FormData(e.currentTarget);
      const content: ContentJson = {
        title: formData.get("title") as string,
        htmlText: formData.get("content") as string,
        keyword: formData.get("keyword") as string,
        subKeywords: (formData.get("subKeywords") as string)
          .split(",")
          .map((k) => k.trim()),
        metaDescription: formData.get("metaDescription") as string,
        languageCode: "en",
        countryCode: "us",
      };

      const seoCheck = new SeoCheck(content, "example.com");
      const result = await seoCheck.analyzeSeo();
      setAnalysis(result);
    } catch (err) {
      setError(
        "Failed to analyze content. Please check your input and try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex w-full justify-center overflow-x-hidden">
      <div className="relative flex min-h-[500px] w-full flex-col items-center">
        <h1 className="mb-4 mt-10 text-center font-heading text-4xl font-bold">
          SEO Analyst
        </h1>
        <p className="mb-4 text-center text-muted-foreground">
          Analyze your SEO content with our fast, user-friendly SEO analyst
          tool. Debug, analyze, and visualize SEO content in seconds with our
          AI SEO tools.
        </p>

        <div className="flex w-full flex-col items-center justify-center gap-8">
          <div
            className={cn(
              "mb-20 mt-8 flex w-full max-w-xl px-2",
              analysis && "mb-8",
            )}
          >
            <form onSubmit={handleUrlSubmit} className="w-full space-y-6">
              <div className="flex w-full flex-col">
                <Label htmlFor="url">Website URL</Label>
                <Input
                  type="url"
                  id="url"
                  name="url"
                  required
                  placeholder="https://example.com/blog-post"
                  className="my-4 h-14 rounded-lg border px-4 text-lg placeholder:italic placeholder:text-muted-foreground/20 dark:bg-secondary"
                />
                <div className="mt-2">
                  <Label htmlFor="targetKeyword">Target Keyword</Label>
                  <Input
                    type="text"
                    id="targetKeyword"
                    name="targetKeyword"
                    required
                    placeholder="SEO"
                    className="my-4 h-14 rounded-lg border px-4 text-lg placeholder:italic placeholder:text-muted-foreground/20 dark:bg-secondary"
                  />
                </div>
                <div className="mt-2">
                  <Label htmlFor="subKeywords">
                    Sub-Keywords (comma-separated)
                  </Label>
                  <Input
                    type="text"
                    id="subKeywords"
                    name="subKeywords"
                    required
                    placeholder="SEO, SEO Tools, SEO Agency"
                    className="my-4 h-14 rounded-lg border px-4 text-lg placeholder:italic placeholder:text-muted-foreground/20 dark:bg-secondary"
                  />
                </div>
              </div>

              <div className="flex justify-center">
                <Button type="submit" disabled={loading}>
                  {loading ? "Analyzing..." : "Analyze SEO"}
                </Button>
              </div>
            </form>

            {error && (
              <div className="mt-4 rounded-md bg-red-50 p-4 text-sm text-red-600">
                {error}
              </div>
            )}
          </div>

          {analysis && (
            <div className="mt-10 flex w-full flex-col items-center bg-secondary pb-20">
              <Separator />
              <div className="mt-10 flex w-full max-w-3xl">
                <Tabs
                  defaultValue="report"
                  className="mt-10 flex w-full flex-col items-center"
                >
                  <TabsList className="mb-4 w-full max-w-xl">
                    <TabsTrigger value="report" className="w-full">
                      Report
                    </TabsTrigger>
                    <TabsTrigger value="content" className="w-full">
                      Content
                    </TabsTrigger>
                  </TabsList>

                  <TabsContent value="report" className="w-full">
                    <div className="mt-10 space-y-6">
                      <Card className="rounded-lg p-6 shadow-md">
                        <CardHeader>
                          <CardTitle>SEO Analysis Results</CardTitle>
                          <CardDescription>
                            <div className="my-6 grid grid-cols-2 gap-4">
                              <div className="rounded-md p-4 text-center">
                                <div className="text-sm">
                                  Overall SEO Score
                                </div>
                                <div className="text-2xl font-bold">
                                  {analysis.seoScore.toFixed(1)}%
                                </div>
                              </div>
                              <div className="rounded-md p-4 text-center">
                                <div className="text-sm">Keyword Score</div>
                                <div className="text-2xl font-bold">
                                  {analysis.keywordSeoScore.toFixed(1)}%
                                </div>
                              </div>
                            </div>
                          </CardDescription>
                        </CardHeader>

                        <div className="mb-6 space-y-2">
                          <div className="flex justify-between border-b py-2">
                            <span className="text-gray-600">Word Count</span>
                            <span className="font-medium">
                              {analysis.wordCount}
                            </span>
                          </div>
                          <div className="flex justify-between border-b py-2">
                            <span className="text-gray-600">
                              Keyword Density
                            </span>
                            <span className="font-medium">
                              {analysis.keywordDensity.toFixed(2)}%
                            </span>
                          </div>
                          <div className="flex justify-between border-b py-2">
                            <span className="text-gray-600">
                              Keyword Frequency
                            </span>
                            <span className="font-medium">
                              {analysis.keywordFrequency}
                            </span>
                          </div>
                        </div>

                        <div className="space-y-4">
                          {analysis.messages.warnings.length > 0 && (
                            <div className="space-y-2">
                              <h3 className="font-medium text-red-600">
                                Warnings
                              </h3>
                              <ul className="list-disc space-y-1 pl-5">
                                {analysis.messages.warnings.map(
                                  (msg: string, i: number) => (
                                    <li
                                      key={i}
                                      className="text-sm text-red-600"
                                    >
                                      {msg}
                                    </li>
                                  ),
                                )}
                              </ul>
                            </div>
                          )}

                          {analysis.messages.goodPoints.length > 0 && (
                            <div className="space-y-2">
                              <h3 className="font-medium text-green-600">
                                Good Points
                              </h3>
                              <ul className="list-disc space-y-1 pl-5">
                                {analysis.messages.goodPoints.map(
                                  (msg: string, i: number) => (
                                    <li
                                      key={i}
                                      className="text-sm text-green-600"
                                    >
                                      {msg}
                                    </li>
                                  ),
                                )}
                              </ul>
                            </div>
                          )}

                          {analysis.messages.minorWarnings.length > 0 && (
                            <div className="space-y-2">
                              <h3 className="font-medium text-yellow-600">
                                Suggestions
                              </h3>
                              <ul className="list-disc space-y-1 pl-5">
                                {analysis.messages.minorWarnings.map(
                                  (msg: string, i: number) => (
                                    <li
                                      key={i}
                                      className="text-sm text-yellow-600"
                                    >
                                      {msg}
                                    </li>
                                  ),
                                )}
                              </ul>
                            </div>
                          )}
                        </div>
                      </Card>
                    </div>
                  </TabsContent>
                  <TabsContent value="content" className="w-full">
                    <div className="mt-10 space-y-6">
                      <Card className="rounded-lg p-6 shadow-md">
                        <CardHeader>
                          <CardTitle>{content?.title}</CardTitle>
                          <CardDescription>
                            <div className="mt-4">
                              {content?.metaDescription}
                            </div>
                          </CardDescription>
                        </CardHeader>
                        <Separator />
                        <CardContent>
                          <div className="mt-8">
                            <Label>Target Keyword:</Label>
                            <div className="ml-6">{content?.keyword}</div>
                          </div>
                          <div className="mt-8">
                            <Label>Sub-Keywords</Label>
                            <div className="ml-6">
                              {content?.subKeywords?.map(
                                (keyword: string) => (
                                  <div key={keyword}>• {keyword}</div>
                                ),
                              )}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                      <Card className="rounded-lg p-6 shadow-md">
                        <CardContent>
                          <div className="mt-8">
                            <div className="prose dark:prose-invert">
                              <ReactMarkdown>
                                {content?.content}
                              </ReactMarkdown>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    </div>
                  </TabsContent>
                </Tabs>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
